import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { ClassValue } from "clsx";
import type { Dispatch, SetStateAction } from "react";

export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs));
}

// Function to format large numbers
export function formatNumber(num: number): string {
  if (num == -1) return "Unlimited";
  if (num >= 1_000_000_000_000)
    return (num / 1_000_000_000_000).toFixed(1) + "T";
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + "B";
  else if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  else if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";

  return num.toLocaleString();
}

export function setFromClipboard(set: Dispatch<SetStateAction<string>>) {
  return () => void navigator.clipboard.readText().then(set);
}

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param base True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export function formatFileSize(
  bytes: number,
  base: "binary" | "decimal" = "binary",
  dp: number = 1,
): string {
  const thresh = base === "decimal" ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units =
    base === "decimal"
      ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
      : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let unit = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++unit;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    unit < units.length - 1
  );

  return bytes.toFixed(dp) + units[unit];
}
